<template>
  <div class="language-chooser" :class="{ active: isOpen }" ref="languageChooser">
    <div class="chooser-toggle" @click="toggleChooser">
      <iconEarth class="icon"></iconEarth>
      <span v-if="languages.length > 1">{{ language.code.toUpperCase() }}&nbsp;/&nbsp;</span>{{ currency.code }}
    </div>
    <div class="chooser-content" v-show="isOpen">
      <div class="row">
        <div class="col-12 col-md-6" v-if="languages.length > 1">
          <div class="form-group">
            <label class="form-group__label">{{ $t('generic.labels.yourLanguage') }}</label>
            <multiselect class="ms-lang" :value="language"
              :options="languages"
              :searchable="false"
              :close-on-select="true"
              :show-labels="false"
              :allow-empty="false"
              :hideSelected="true"
              @input="setLanguage" track-by="name" label="name">
              <template slot="singleLabel" slot-scope="{ option }">{{ option.name }}</template>
            </multiselect>
          </div>
        </div>
        <div class="col-12 col-md-6">
          <div class="form-group">
            <div class="d-flex align-items-center">
              <label class="form-group__label">{{ $t('generic.labels.yourCurrency') }}</label>
              <CiTooltip class="tooltip" :direction="'down'">
                <template slot="title">{{ $t('tooltips.currency.title') }}</template>
                <template slot="content">
                  <p v-html="$t('tooltips.currency.content')"></p>
                </template>
              </CiTooltip>
            </div>
            <multiselect
              v-if="currencies.length > 1"
              :value="currency"
              :options="currencies"
              :searchable="false"
              :close-on-select="true"
              :show-labels="false"
              :allow-empty="false"
              :hideSelected="true"
              @input="setCurrency" track-by="code" label="code">
              <template slot="singleLabel" slot-scope="{ option }">{{ option.name }} ({{ option.code }})</template>
              <template slot="option" slot-scope="{ option }">{{ option.name }} ({{ option.code }})</template>
            </multiselect>
            <div
              v-else
              class="single-select"
            >
              {{ currency.name }} ({{ currency.code }})
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import iconEarth from '@/assets/icons/flag-earth.svg'
import { CiTooltip } from '@/components'

export default {
  name: 'CiLanguageChooser',
  components: {
    CiTooltip,
    iconEarth,
  },
  props: {
    language: {
      type: Object,
      required: true,
    },
    currency: {
      type: Object,
      required: true,
    },
    languages: {
      type: Array,
      required: true,
    },
    currencies: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isOpen: false,
    }
  },
  created() {
    window.addEventListener('click', this.onClickOutside)
    window.addEventListener('touchstart', this.onClickOutside)
  },
  methods: {
    onClickOutside(event) {
      /* istanbul ignore else */
      if (!this.isOpen) return

      /* istanbul ignore else */
      if (
        event.target !== this.$refs.languageChooser &&
        this.$refs.languageChooser &&
        !this.$refs.languageChooser.contains(event.target)
      ) {
        this.isOpen = false
      }
    },

    toggleChooser() {
      this.isOpen = !this.isOpen
    },

    setLanguage(value) {
      if (value) {
        this.$emit('setLanguage', value)
      }
    },

    setCurrency(value) {
      if (value) {
        this.$emit('setCurrency', value)
      }
    },
  },

  beforeDestroy() {
    window.removeEventListener('click', this.onClickOutside)
    window.removeEventListener('touchstart', this.onClickOutside)
  },
}
</script>
<style lang="scss" scoped>
.language-chooser {
  display: flex;
  justify-content: flex-end;
  margin: 0;
  overflow: visible;
  position: absolute;
  right: 0;
  top: 20px;
  width: auto;
  z-index: 10;

  @include media-breakpoint-up(md) {
    top: 55px;
    right: 20px;
  }

  @include media-breakpoint-up(lg) {
    top: 25px;
    right: 30px;
  }

  @include media-breakpoint-up(xl) {
    top: 60px;
  }

  &.active {
    width: 100%;
    z-index: 20;

    @include media-breakpoint-up(md) {
      width: calc(100% - 40px);
    }

    @include media-breakpoint-up(lg) {
      width: calc(100% - 60px);
    }
  }
}

.chooser-toggle {
  @include font-format(0.625rem, 0.938rem);

  align-items: center;
  background-color: white;
  border-radius: 15px;
  cursor: pointer;
  display: inline-flex;
  height: 30px;
  padding: 0 7px;

  .icon {
    margin: 0 7px 0 0;
  }
}

.chooser-content {
  background: white;
  border-radius: 4px;
  border: 1px solid var(--primary-color);
  bottom: -20px;
  left: 0;
  padding: 20px 20px 0;
  position: absolute;
  transform: translate3d(0, 100%, 0);
  width: 100%;
  z-index: 200;

  &::before {
    border-bottom: solid 12px var(--primary-color);
    border-left: solid 12px transparent;
    border-right: solid 12px transparent;
    top: -12px;
    content: '';
    height: 0;
    position: absolute;
    right: 30px;
    transform: translate(50%, 0);
    width: 0;
    z-index: 10;
  }

  &::after {
    border-bottom: solid 11px white;
    border-left: solid 11px transparent;
    border-right: solid 11px transparent;
    top: -11px;
    content: '';
    height: 0;
    position: absolute;
    right: 30px;
    transform: translate(50%, 0);
    width: 0;
    z-index: 10;
  }

  .tooltip {
    margin-bottom: 0.5rem;
  }

  .single-select {
    padding: 20px 10px 16px;
    color: $gray-text;
    background-color: $gray;
  }
}
</style>
